import React, { useState, useEffect } from "react";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import { ClipLoader } from "react-spinners";
import { FaUpload, FaFileExport } from "react-icons/fa";
import Ajv from "ajv";
import addFormats from "ajv-formats";
import { jsonrepair } from "jsonrepair";
import jsBeautify from "js-beautify";

const JSONSchemaValidator = () => {
  const [jsonData, setJsonData] = useState("");
  const [jsonSchema, setJsonSchema] = useState("");
  const [validationResult, setValidationResult] = useState("");
  const [errorDetails, setErrorDetails] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [lineNumbersData, setLineNumbersData] = useState([]);
  const [lineNumbersSchema, setLineNumbersSchema] = useState([]);

  const exampleJSON = `{
    "company": "SFDC247",
    "employees": [
      {
        "id": 1,
        "name": "John Doe",
        "role": "Salesforce Developer",
        "skills": ["Apex", "Visualforce", "LWC", "JavaScript"],
        "details": {
          "years_of_experience": 5,
          "certifications": ["Admin", "Platform Developer I", "Platform Developer II"]
        }
      },
      {
        "id": 2,
        "name": "Jane Smith",
        "role": "Salesforce Architect",
        "skills": ["Integration", "API Design", "Data Modeling"],
        "details": {
          "years_of_experience": 10,
          "certifications": ["System Architect", "Application Architect"]
        }
      }
    ]
  }`;

  const exampleSchema = `{
    "type": "object",
    "properties": {
      "company": { "type": "string" },
      "employees": {
        "type": "array",
        "items": {
          "type": "object",
          "properties": {
            "id": { "type": "integer" },
            "name": { "type": "string" },
            "role": { "type": "string" },
            "skills": {
              "type": "array",
              "items": { "type": "string" }
            },
            "details": {
              "type": "object",
              "properties": {
                "years_of_experience": { "type": "integer" },
                "certifications": {
                  "type": "array",
                  "items": { "type": "string" }
                }
              },
              "required": ["years_of_experience", "certifications"]
            }
          },
          "required": ["id", "name", "role", "skills", "details"]
        }
      }
    },
    "required": ["company", "employees"]
  }`;

  const handleFileUpload = (event, setTarget, setLineNumbers) => {
    const file = event.target.files[0];
    if (file) {
      setProcessing(true); // Show spinner
      const reader = new FileReader();
      reader.onload = (e) => {
        const fileContent = e.target.result;
        setTarget(fileContent);
        setLineNumbers(fileContent.split("\n").map((_, index) => index + 1));
        setProcessing(false); // Hide spinner
        event.target.value = ""; // Reset file input to allow re-uploading the same file
      };
      reader.onerror = () => {
        console.error("Error reading file");
        setProcessing(false); // Hide spinner even on error
        event.target.value = ""; // Reset file input
      };
      reader.readAsText(file);
    }
  };

  useEffect(() => {
    setLineNumbersData(jsonData.split("\n").map((_, index) => index + 1));
  }, [jsonData]);

  useEffect(() => {
    setLineNumbersSchema(jsonSchema.split("\n").map((_, index) => index + 1));
  }, [jsonSchema]);

  const validateSchema = () => {
    setProcessing(true);
    setTimeout(() => {
      try {
        const parsedData = JSON.parse(jsonData);
        const parsedSchema = JSON.parse(jsonSchema);

        // Initialize Ajv
        const ajv = new Ajv({ allErrors: true, strict: false });
        addFormats(ajv);

        const validate = ajv.compile(parsedSchema);
        const valid = validate(parsedData);

        if (valid) {
          setValidationResult("✅ Valid JSON Data");
          setErrorDetails([]);
        } else {
          setValidationResult("❌ Invalid JSON Data");
          setErrorDetails(
            validate.errors.map((error) => ({
              instancePath: error.instancePath || "N/A",
              message: error.message,
            }))
          );
        }
      } catch (err) {
        setValidationResult("❌ Invalid JSON or Schema");
        setErrorDetails([{ instancePath: "N/A", message: err.message }]);
      } finally {
        setProcessing(false);
      }
    }, 500);
  };

  const fixJSON = (isData) => {
    try {
      const input = isData ? jsonData : jsonSchema;
      const repairedJSON = jsonrepair(input);
      const beautifiedJSON = jsBeautify(repairedJSON, { indent_size: 2 });
      if (isData) {
        setJsonData(beautifiedJSON);
      } else {
        setJsonSchema(beautifiedJSON);
      }
      setValidationResult("✅ JSON Fixed and Beautified");
      setErrorDetails([]);
    } catch (err) {
      setValidationResult("❌ Fix Failed");
      setErrorDetails([{ instancePath: "N/A", message: err.message }]);
    }
  };

  const clearInputs = () => {
    setJsonData("");
    setJsonSchema("");
    setValidationResult("");
    setErrorDetails([]);
  };

  const useExampleData = () => {
    setJsonData(exampleJSON);
    setJsonSchema(exampleSchema);
    setValidationResult("");
    setErrorDetails([]);
  };

  const handleExport = (content, filename) => {
    const blob = new Blob([content], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const renderInputWithLineNumbers = (
    textareaValue,
    onChangeHandler,
    lineNumbers,
    placeholder,
    handleUpload,
    uploadId
  ) => (
    <ScrollSyncPane>
      <div style={{ display: "flex", position: "relative", height: "300px" }}>
        <div
          style={{
            overflowY: "auto",
            backgroundColor: "#f8f9fa",
            borderRight: "1px solid #ccc",
            textAlign: "right",
            userSelect: "none",
            color: "#6c757d",
            padding: "5px",
            width: "40px",
            boxSizing: "border-box",
          }}
        >
          {lineNumbers.map((number) => (
            <div key={number} style={{ height: "1.5em", lineHeight: "1.5em" }}>
              {number}
            </div>
          ))}
        </div>
        <textarea
          className="form-control"
          value={textareaValue}
          onChange={(e) => onChangeHandler(e.target.value)}
          placeholder={placeholder}
          style={{
            flex: "1",
            height: "100%",
            resize: "none",
            border: "2px solid #ccc",
            borderRadius: "0",
            overflowY: "auto",
            fontFamily: "monospace",
          }}
        />
        <label
          htmlFor={uploadId}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: "pointer",
          }}
        >
          <FaUpload size={20} color="#000000" />
        </label>
        <input
          type="file"
          id={uploadId}
          style={{ display: "none" }}
          accept=".json"
          onChange={handleUpload}
        />
      </div>
    </ScrollSyncPane>
  );

  return (
    <div className="container mt-4">
      <div className="text-center mb-3">
        <button className="btn btn-info" onClick={useExampleData}>
          Use Example Data
        </button>
      </div>
      <ScrollSync>
        <div className="row">
          <div className="col-md-6">
            {renderInputWithLineNumbers(
              jsonData,
              setJsonData,
              lineNumbersData,
              "Enter JSON Data here...",
              (event) => handleFileUpload(event, setJsonData, setLineNumbersData),
              "upload-data"
            )}
            <div className="text-center mt-2">
              <button className="btn btn-warning me-2" onClick={() => fixJSON(true)}>
                Fix JSON
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => handleExport(jsonData, "data.json")}
              >
                <FaFileExport /> Export JSON Data
              </button>
            </div>
          </div>
          <div className="col-md-6">
            {renderInputWithLineNumbers(
              jsonSchema,
              setJsonSchema,
              lineNumbersSchema,
              "Enter JSON Schema here...",
              (event) => handleFileUpload(event, setJsonSchema, setLineNumbersSchema),
              "upload-schema"
            )}
            <div className="text-center mt-2">
              <button className="btn btn-warning me-2" onClick={() => fixJSON(false)}>
                Fix Schema
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => handleExport(jsonSchema, "schema.json")}
              >
                <FaFileExport /> Export JSON Schema
              </button>
            </div>
          </div>
        </div>
      </ScrollSync>
      <div className="text-center mt-3">
        <button
          className="btn btn-success me-2"
          onClick={validateSchema}
          disabled={processing}
        >
          {processing ? (
            <>
              <ClipLoader size={15} color="white" />
              Validating...
            </>
          ) : (
            "Validate JSON"
          )}
        </button>
        <button className="btn btn-secondary" onClick={clearInputs}>
          Clear
        </button>
      </div>
      {validationResult && (
        <p
          className={`text-center mt-3 ${
            validationResult.includes("Valid") ? "text-success" : "text-danger"
          }`}
        >
          {validationResult}
        </p>
      )}
      {errorDetails.length > 0 && (
        <div className="mt-3">
          <h5>Error Details</h5>
          <ul>
            {errorDetails.map((err, index) => (
              <li key={index} style={{ marginBottom: "10px" }}>
                <strong>Instance Path:</strong> {err.instancePath || "N/A"} <br />
                <strong>Message:</strong> {err.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default JSONSchemaValidator;